import { useState } from "react";

/** Живой styleguide — рендерит реальные mt-* компоненты из styles/.
 *
 * Раньше тут был iframe на статичный мокап (/static/redesign/styleguide.html),
 * который к тому же нигде не отдавался → 404. Живая страница на тех же CSS
 * классах, что и весь интерфейс: ловит регрессии вроде невидимого текста
 * primary-кнопки на hover. */

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rf-section" style={{ marginBottom: "var(--sp-3)" }}>
      <div className="rf-section__head">
        <span>{title}</span>
        {note && (
          <>
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span className="mt-text-tight mt-muted">{note}</span>
          </>
        )}
      </div>
      <div className="rf-section__body">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "8px 0",
        borderBottom: "1px solid var(--border-subtle, #e4e7ec)",
      }}
    >
      <span
        className="mt-text-tight mt-muted"
        style={{ width: 130, flexShrink: 0 }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

const BTN_VARIANTS: ReadonlyArray<{ cls: string; label: string }> = [
  { cls: "mt-btn--primary", label: "primary" },
  { cls: "mt-btn--secondary", label: "secondary" },
  { cls: "mt-btn--tertiary", label: "tertiary" },
  { cls: "mt-btn--ghost", label: "ghost" },
  { cls: "mt-btn--danger", label: "danger" },
  { cls: "mt-btn--danger-solid", label: "danger-solid" },
];

const BTN_SIZES: ReadonlyArray<{ cls: string; label: string }> = [
  { cls: "mt-btn--sm", label: "sm" },
  { cls: "", label: "default" },
  { cls: "mt-btn--lg", label: "lg" },
];

const BADGE_VARIANTS: ReadonlyArray<string> = [
  "neutral",
  "info",
  "success",
  "warning",
  "danger",
];

export default function Styleguide() {
  const [inputValue, setInputValue] = useState("Пациент с гипертонией");

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1000 }}>
      <header style={{ marginBottom: "var(--sp-3)" }}>
        <h1>Styleguide</h1>
        <p className="mt-text-tight mt-muted">
          Живая витрина mt-компонентов на тех же CSS, что и весь интерфейс.
          Наведи курсор на кнопки — hover-состояния настоящие.
        </p>
      </header>

      <Section title="Кнопки — варианты × размеры" note="components.css §2">
        {BTN_VARIANTS.map((v) => (
          <Row key={v.cls} label={v.label}>
            {BTN_SIZES.map((s) => (
              <button
                key={s.label}
                type="button"
                className={`mt-btn ${v.cls} ${s.cls}`.trim()}
              >
                {s.label}
              </button>
            ))}
            <button
              type="button"
              className={`mt-btn ${v.cls}`.trim()}
              disabled
            >
              disabled
            </button>
          </Row>
        ))}
      </Section>

      <Section title="Бейджи" note="components.css §5">
        <Row label="обычные">
          {BADGE_VARIANTS.map((v) => (
            <span key={v} className={`mt-badge mt-badge--${v}`}>
              {v}
            </span>
          ))}
        </Row>
        <Row label="solid">
          {["success", "warning", "danger"].map((v) => (
            <span
              key={v}
              className={`mt-badge mt-badge--solid mt-badge--${v}`}
            >
              {v}
            </span>
          ))}
        </Row>
        <Row label="без точки">
          {BADGE_VARIANTS.map((v) => (
            <span
              key={v}
              className={`mt-badge mt-badge--no-dot mt-badge--${v}`}
            >
              {v}
            </span>
          ))}
        </Row>
      </Section>

      <Section title="Поля ввода" note="components.css §1">
        <Row label="mt-input">
          <input
            className="mt-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            style={{ minWidth: 280 }}
          />
        </Row>
        <Row label="sm / mono">
          <input className="mt-input mt-input--sm" defaultValue="small" />
          <input className="mt-input mt-input--mono" defaultValue="mono 12.5" />
        </Row>
        <Row label="invalid">
          <input
            className="mt-input mt-input--invalid"
            defaultValue="ошибка валидации"
          />
        </Row>
        <Row label="disabled">
          <input className="mt-input" defaultValue="disabled" disabled />
        </Row>
        <Row label="mt-field">
          <label className="mt-field" style={{ minWidth: 280 }}>
            <span className="mt-field__label">Поле с подписью</span>
            <input className="mt-input" defaultValue="значение" />
          </label>
        </Row>
      </Section>

      <Section title="Карточки и состояния" note="components.css §4">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          <div
            className="mt-card"
            style={{
              padding: "12px 14px",
              border: "1px solid var(--border-default, #d0d5dd)",
              borderRadius: 8,
            }}
          >
            <div className="mt-text-tight mt-muted">mt-card</div>
            <div style={{ fontSize: "var(--fs-xl, 24px)", fontWeight: 600 }}>
              42
            </div>
          </div>
          <div className="mt-empty">mt-empty — пусто</div>
          <div className="mt-empty mt-empty--error">
            mt-empty--error — ошибка
          </div>
        </div>
      </Section>

      <Section title="Типографика-хелперы">
        <Row label="mt-muted">
          <span className="mt-muted">приглушённый текст</span>
        </Row>
        <Row label="mt-text-tight">
          <span className="mt-text-tight">плотный межстрочный</span>
        </Row>
        <Row label="mt-mono">
          <span className="mt-mono">monospace 0xDEADBEEF</span>
        </Row>
      </Section>
    </div>
  );
}
